import _ from "underscore";
import { MobileActions } from "../../store/mobile/actions";
import pageHTML from "./featureFlags.tmpl";
import DebugPageView from "./debugPageView";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { IFeatureFlagStatus } from "../../store/data/types";
import {
    isFeatureFlagOverrideAllowed,
    getFeatureFlagOverrides,
    setFeatureFlagOverride,
    clearFeatureFlagOverride,
    clearAllFeatureFlagOverrides
} from "../../utils/featureFlagOverrides";

export default class FeatureFlagsView extends DebugPageView {
    private readonly template = _.template(pageHTML);

    render() {
        const allFromStore = this.app.featureFlagService.getAllFeatureFlags();
        const inStore = new Set(allFromStore.map(f => f.name));
        const overrides = getFeatureFlagOverrides();
        const canOverride = isFeatureFlagOverrideAllowed();
        const originalFlags = this.app.store.getState().data.originalFeatureFlags;

        const enabled = allFromStore
            .filter(f => f.isEnabled)
            .sort((a, b) => a.name.localeCompare(b.name));

        const disabledFromStore = allFromStore
            .filter(f => !f.isEnabled)
            .sort((a, b) => a.name.localeCompare(b.name));

        const disabledNotInStore: IFeatureFlagStatus[] = (Object.keys(FeatureFlagName) as (keyof typeof FeatureFlagName)[])
            .map(k => FeatureFlagName[k])
            .filter(name => !inStore.has(name))
            .sort()
            .map(name => ({ name, isEnabled: false }));

        const disabled = [...disabledFromStore, ...disabledNotInStore]
            .sort((a, b) => a.name.localeCompare(b.name));

        this.el.innerHTML = this.template({
            enabledCount: enabled.length,
            disabledCount: disabled.length
        });

        this.app.store.dispatch(MobileActions.setPageState({
            title: "Feature Flags",
            pageLevel: 2
        }));

        const enabledList = this.el.querySelector("#debug-featureflags__enabled-list");
        const disabledList = this.el.querySelector("#debug-featureflags__disabled-list");

        enabled.forEach(flag => enabledList?.appendChild(this.buildEntry(flag, canOverride, overrides, originalFlags)));
        disabled.forEach(flag => disabledList?.appendChild(this.buildEntry(flag, canOverride, overrides, originalFlags)));

        // Setup action buttons
        const resetAllBtn = this.el.querySelector("#debug-featureflags__reset-all") as HTMLButtonElement;
        const enableReleaseBtn = this.el.querySelector("#debug-featureflags__enable-release") as HTMLButtonElement;

        if (canOverride) {
            resetAllBtn.disabled = Object.keys(overrides).length === 0;
            resetAllBtn.addEventListener("click", () => {
                clearAllFeatureFlagOverrides();
                this.render();
            });

            const allReleaseFlagsEnabled = this.areAllReleaseFlagsEnabled(enabled);
            enableReleaseBtn.disabled = allReleaseFlagsEnabled;
            enableReleaseBtn.addEventListener("click", () => {
                this.enableAllReleaseFlags();
                this.render();
            });
        } else {
            resetAllBtn.disabled = true;
            enableReleaseBtn.disabled = true;
        }

        return this;
    }

    private enableAllReleaseFlags(): void {
        // Enable all flags that contain ".RELEASE" in their name
        const allFlags = (Object.keys(FeatureFlagName) as (keyof typeof FeatureFlagName)[])
            .map(k => FeatureFlagName[k]);
        allFlags
            .filter(name => name.includes(".RELEASE"))
            .forEach(name => setFeatureFlagOverride(name, true));
    }

    private areAllReleaseFlagsEnabled(enabledFlags: IFeatureFlagStatus[]): boolean {
        const enabledNames = new Set(enabledFlags.map(f => f.name));
        const releaseFlags = (Object.keys(FeatureFlagName) as (keyof typeof FeatureFlagName)[])
            .map(k => FeatureFlagName[k])
            .filter(name => name.includes(".RELEASE"));
        return releaseFlags.every(name => enabledNames.has(name));
    }

    private buildEntry(
        flag: IFeatureFlagStatus,
        canOverride: boolean,
        overrides: Record<string, boolean>,
        originalFlags: Record<string, IFeatureFlagStatus>
    ): HTMLElement {
        const entry = document.createElement("div");
        entry.className = "debug-featureflags__entry";

        const info = document.createElement("div");
        info.className = "debug-featureflags__info";

        const nameEl = document.createElement("div");
        nameEl.className = "debug-featureflags__name";
        nameEl.textContent = flag.name;
        info.appendChild(nameEl);

        const hasOverride = canOverride && overrides[flag.name] !== undefined;
        const originalValue = originalFlags[flag.name]?.isEnabled;

        const valueEl = document.createElement("div");
        valueEl.className = "debug-featureflags__value";
        if (hasOverride) {
            const serverValue = originalValue !== undefined ? originalValue : "not set";
            valueEl.textContent = `${ flag.isEnabled } (override, server: ${ serverValue })`;
        } else {
            valueEl.textContent = flag.value !== undefined ? String(flag.value) : String(flag.isEnabled);
        }
        info.appendChild(valueEl);

        entry.appendChild(info);

        const btn = document.createElement("button");
        btn.className = "debug-featureflags__override";

        // Only enable override buttons when debug mode is on
        if (canOverride) {
            btn.disabled = false;

            if (hasOverride) {
                btn.textContent = "Reset";
                btn.classList.add("debug-featureflags__override--active");
                btn.addEventListener("click", () => {
                    clearFeatureFlagOverride(flag.name);
                    this.render();
                });
            } else {
                btn.textContent = flag.isEnabled ? "Disable" : "Enable";
                btn.addEventListener("click", () => {
                    setFeatureFlagOverride(flag.name, !flag.isEnabled);
                    this.render();
                });
            }
        } else {
            btn.textContent = flag.isEnabled ? "Disable" : "Enable";
            btn.disabled = true;
        }

        entry.appendChild(btn);

        return entry;
    }
}
